import { useState } from 'react';
import { UserPlus } from 'lucide-react';
import { Button } from '@/components/button';
import { Dialog } from '@/components/dialog';
import { Input } from '@/components/input';
import { Select } from '@/components/select';
import { useToast } from '@/components/toast';
import { useAddContactEmail, useContacts, useCreateContact } from '@/lib/contacts';

const NEW_CONTACT = 'new';

/**
 * Offered after an email goes to an address that is not on any contact. Keeps
 * the contact list catching up with reality instead of drifting, so the next
 * send can preselect the right person.
 */
export function SaveAddressDialog({
  clientId,
  addresses,
  onDone,
}: {
  clientId: number;
  addresses: string[];
  onDone: () => void;
}) {
  const [index, setIndex] = useState(0);
  const [target, setTarget] = useState(NEW_CONTACT);
  const [name, setName] = useState('');
  const [pending, setPending] = useState(false);

  const contacts = useContacts(clientId);
  const addEmail = useAddContactEmail(clientId);
  const createContact = useCreateContact(clientId);
  const toast = useToast();

  const address = addresses[index];
  if (!address) return null;

  function next() {
    setTarget(NEW_CONTACT);
    setName('');
    if (index + 1 < addresses.length) {
      setIndex(index + 1);
    } else {
      onDone();
    }
  }

  async function save() {
    setPending(true);
    try {
      if (target === NEW_CONTACT) {
        await createContact.mutateAsync({
          name: name.trim() || address,
          emails: [{ label: 'work', address, is_primary: true }],
        });
      } else {
        const contact = contacts.data?.find((c) => String(c.id) === target);
        if (!contact) return;
        await addEmail.mutateAsync({ contact, address });
      }
      toast.success(`${address} saved to contacts`);
      next();
    } catch (err) {
      toast.error('Could not save the address', err instanceof Error ? err.message : undefined);
    } finally {
      setPending(false);
    }
  }

  return (
    <Dialog open onClose={onDone} title="Save this address?">
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">
          <span className="font-medium text-[var(--color-foreground)]">{address}</span> is not on any
          contact for this client. Saving it means you can pick it by name next time.
        </p>

        <div className="space-y-2">
          <Select value={target} onChange={(e) => setTarget(e.target.value)}>
            <option value={NEW_CONTACT}>New contact</option>
            {(contacts.data ?? []).map((c) => (
              <option key={c.id} value={String(c.id)}>
                Add to {c.name}
              </option>
            ))}
          </Select>

          {target === NEW_CONTACT ? (
            <Input
              placeholder="Contact name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          ) : null}
        </div>

        <div className="flex items-center justify-between gap-3 border-t border-[var(--color-border)] pt-3">
          <p className="text-xs text-muted-foreground">
            {addresses.length > 1 ? `${index + 1} of ${addresses.length}` : ''}
          </p>
          <div className="flex gap-2">
            <Button variant="secondary" onClick={next} disabled={pending}>
              Not now
            </Button>
            <Button
              onClick={save}
              disabled={pending || (target === NEW_CONTACT && !name.trim())}
            >
              <UserPlus className="size-4" /> {pending ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </div>
      </div>
    </Dialog>
  );
}
